import {IconButton} from '@mui/material';
import {styled, Theme} from '@mui/material/styles';
import Tooltip, {tooltipClasses, TooltipProps} from '@mui/material/Tooltip';
import {
  DataGrid,
  DataGridProps,
  GridCellParams,
  GridColDef,
  GridRenderCellParams,
  GridRowHeightParams,
  GridRowParams,
  GridSortModel,
  useGridApiContext,
} from '@mui/x-data-grid';
import {t} from 'i18next';
import React, {
  isValidElement,
  memo,
  ReactElement,
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import {ReactComponent as SortArrowDown} from '../../../assests/svg/sort-arrow-down.svg';
import {ReactComponent as SortArrowUp} from '../../../assests/svg/sort-arrow-up.svg';
import {ReactComponent as SortIcon} from '../../../assests/svg/sorting_arrow.svg';
import {AurDataGridColDef, SortDirection} from '../../../types/types';
import {TreeViewRowData} from '../../../utils/inTakeAuditReportUtils';
import {
  moveParentDataToChildren,
  restoreOriginalData,
  scrollToGridView,
} from '../../../utils/utils';
import AurCheckbox from '../aur-checkbox/AurCheckbox';
import AurDataGridNoRowsOverlay from '../aur-data-grid-no-rows-overlay/AurDataGridNoRowsOverlay';
import AurPagination from '../aur-pagination/AurPagination';
import AurSearchBox from '../aur-search-box/AurSearchBox';
import AurCustomSort from './AurCustomSort';
import {
  INDEXING_HISTORY,
  INDEXING_SUB_INFO,
  INDEXING_SUBINDEX_HEIGHT,
} from '../../../constants/constants';

const LightTooltip = styled(({className, ...props}: TooltipProps) =>
  props?.title ? (
    <Tooltip
      {...props}
      classes={{popper: className, arrow: 'custom-arrow'}}
      placement="top"
      arrow
    />
  ) : null
)(({theme}: {theme: Theme}) => ({
  [`& .${tooltipClasses.tooltip}`]: {
    backgroundColor: theme.palette.common.white,
    color: 'rgba(0, 0, 0, 0.87)',
    boxShadow: theme.shadows[1],
    fontSize: 11,
    marginTop: -11,
  },
}));
type AurDataGridProps = DataGridProps & {
  entityType?: string;
  checkBoxVisible?: boolean;
  className?: string;
  selectedIds?: (number | string)[];
  rowLimitForPaginationHide?: number; // row count exceeding this limit will make pagination visible
  onSelectionChange?: (selectedIds: (number | string)[]) => void;
  currentPage: number;
  setCurrentPage?: (arg0: number) => void;
  itemsPerPage: number;
  setItemsPerPage?: (arg0: number) => void;
  hideFooterSelectedRowCount?: boolean; // true => won't show total selected rows count
  defaultSortColumnName?: string;
  defaultSortOrder?: 'asc' | 'desc';
  onPageChange?: (pagenumber: number) => void;
  onPageSizeChange?: (pageSize: number) => void;
  totalRowCount?: number;
  handleRowClick?: (params: GridRowParams) => void; // to handle row click
  pointer?: boolean;
  primaryKey?: string; // Unique key property name per row
  setSortModel?: (sortModel: GridSortModel) => void;
  includeCancelContinueEvents?: boolean;
  showPagination?: boolean;
  onSearch?: (searchTerm: string) => void;
  searchValue?: string;
  footerChildComponent?: ReactElement; // render component under custom pagination footer
  isTreeView?: boolean;
  noRowsOverlay?: React.JSXElementConstructor<any>; // custom overlay to show when there is no rows
  searchLoading?: boolean;
  minItemForPagination?: number;
  visibleToolTip?: boolean;
  // Props for Error overlay
  onRefresh?: () => void;
  containerHeight?: number;
  headerSpanTabIndex?: number;
  defaultHeaderCellTabIndex?: number;
  enableRowClickOnEnterPress?: boolean;
  disableHeaderRowTabIndex?: boolean;
  indexTree?: boolean;
  trackPaginationActionName?: string;
  addedInDialog?: boolean; // to track datagrid visible on Dialog then use internal grid scroll when page change
  classNameToFindElement?: string; // this used to find grid using class selector to scroll on page change
  classNameOfParentScollElement?: string; // In dialog, scroll control by parent component so need to fire scroll on it when page change
};

export const AurDataGrid = (props: AurDataGridProps) => {
  const {
    rows = [],
    columns,
    pageSizeOptions = [],
    entityType,
    checkBoxVisible = true,
    className,
    onSelectionChange,
    selectedIds,
    rowLimitForPaginationHide = 0,
    currentPage,
    setCurrentPage,
    itemsPerPage,
    setItemsPerPage,
    hideFooterSelectedRowCount = true,
    defaultSortColumnName = '',
    defaultSortOrder = 'asc',
    onPageChange,
    onPageSizeChange,
    totalRowCount,
    handleRowClick,
    pointer = false,
    setSortModel,
    sortModel,
    primaryKey = 'id', // Unique key property name per row. Default is "id"
    includeCancelContinueEvents = false,
    showPagination = true,
    onSearch,
    searchValue,
    footerChildComponent,
    isTreeView = false,
    noRowsOverlay = AurDataGridNoRowsOverlay,
    searchLoading,
    visibleToolTip,
    minItemForPagination = 25,
    onRefresh,
    containerHeight,
    headerSpanTabIndex,
    defaultHeaderCellTabIndex,
    enableRowClickOnEnterPress,
    disableHeaderRowTabIndex,
    indexTree = false,
    trackPaginationActionName,
    addedInDialog = false,
    classNameToFindElement,
    classNameOfParentScollElement,
    ...restProps
  } = props;
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null); // For dropdown
  const [activeColumn, setActiveColumn] = useState<string | null>(null);
  const totalItems = totalRowCount ? totalRowCount : rows.length;
  const shouldHidePagination =
    rows.length === 0 ||
    (rowLimitForPaginationHide != 0 &&
      rows.length <= rowLimitForPaginationHide) ||
    totalItems <= minItemForPagination;
  const previousPage = React.useRef(currentPage);
  const previousItemPerPage = React.useRef(itemsPerPage);
  const MemoCheckbox = React.memo(AurCheckbox);
  const renderCheckbox = useCallback(
    (props: any) => <MemoCheckbox {...props} data-testid="checkbox" />,
    []
  );

  useEffect(() => {
    if (disableHeaderRowTabIndex) {
      /**
       * Manipulating DataGrid Columns Header div to make it not focusable using keyboard tab press event.
       * Default its enabled but its creating issue as user need to presse tab two time to reach focus state in
       * search under header of datagrid table
       */
      setTimeout(() => {
        const headerElements = document.querySelectorAll(
          '.MuiDataGrid-columnHeader'
        );
        headerElements.forEach((el) => el.setAttribute('tabindex', '-1'));
      }, 750);
    }
  }, [disableHeaderRowTabIndex]);

  useEffect(() => {
    if (
      (previousPage.current !== currentPage ||
        previousPage.current !== itemsPerPage) &&
      rows.length > 0
    ) {
      previousPage.current = currentPage;
      previousItemPerPage.current = itemsPerPage;

      if (addedInDialog) {
        // If showing in dialog then need to fine scrolling container and scroll it as
        // we can't perform scroll on window when dialog open
        const parentScrollElement = document.querySelector(
          `.${classNameOfParentScollElement}`
        ) as HTMLElement | null;
        // Added time out to make sure grid updated correclty with data before scroll
        setTimeout(() => {
          requestAnimationFrame(() => {
            parentScrollElement?.scrollTo({
              top: 0,
              behavior: 'auto',
            });
          });
        }, 300);
        return;
      }
      scrollToGridView(classNameToFindElement ?? 'aur-datagrid', true);
    }
  }, [rows]);

  const perPageOptions = useMemo(() => {
    const options = (pageSizeOptions as number[]).filter(
      (option) => option <= totalItems
    );
    const additionalOption = (pageSizeOptions as number[]).find(
      (option) => option >= totalItems
    );
    if (additionalOption && !options.includes(additionalOption)) {
      options.push(additionalOption);
    }
    return options;
  }, [pageSizeOptions, totalItems]);

  const CustomPagination: React.FC = () => {
    const apiRef = useGridApiContext();

    const handlePageChange = (page: number) => {
      apiRef.current.setPage(page - 1);
      setCurrentPage?.(page);
      onPageChange?.(page);
    };

    const setItemsPerPageHandler = (itemsPerPage: number) => {
      setItemsPerPage?.(itemsPerPage);
      onPageSizeChange?.(itemsPerPage);
      apiRef.current.setPage(0);
      setCurrentPage?.(1);
      onPageChange?.(1);
      apiRef.current.setPageSize(itemsPerPage);
    };

    return totalItems > 0 && showPagination ? (
      <div className="aur-datagrid-footer-child-container">
        {footerChildComponent ? footerChildComponent : null}
        <AurPagination
          totalItems={totalItems}
          itemsPerPage={itemsPerPage}
          currentPage={currentPage}
          perPageOptions={perPageOptions}
          itemType={entityType}
          setItemsPerPage={setItemsPerPageHandler}
          onPageChange={handlePageChange}
          includeCancelContinueEvents={includeCancelContinueEvents}
          trackPaginationActionName={trackPaginationActionName}
        />
      </div>
    ) : (
      <div className="aur-datagrid-footer-child-container">
        {footerChildComponent ? footerChildComponent : null}
      </div>
    );
  };

  // Function to handle sorting
  const handleSort = (field: string, direction: SortDirection) => {
    setSortModel ? setSortModel([{field, sort: direction || null}]) : '';
    setAnchorEl(null);
  };

  const handleCellKeyDown = (_: GridCellParams, event: React.KeyboardEvent) => {
    if (event.key === 'Enter' && enableRowClickOnEnterPress) {
      event.preventDefault(); // Prevent the default Enter behavior
      /**
       * On Press of enter key, find active row and fire the onclick event for it
       */
      const focusedRow = document.activeElement?.closest(
        '.MuiDataGrid-row'
      ) as HTMLDivElement;
      if (focusedRow) {
        focusedRow.click();
      }
    }
  };

  const renderSortIcon = (params: any) => {
    let iconButton: React.ReactNode = null;
    const sortDirection = sortModel?.find(
      (sort) => sort.field === params.field
    )?.sort;

    let sortIcon: React.ReactNode = (
      <SortIcon aria-hidden className="custom-sort-icon" />
    );
    if (sortDirection === 'asc') {
      sortIcon = <SortArrowUp aria-hidden className="custom-sort-icon" />;
    } else if (sortDirection === 'desc') {
      sortIcon = <SortArrowDown aria-hidden className="custom-sort-icon" />;
    }
    if (params.colDef.sortable) {
      iconButton = (
        <IconButton
          onClick={(event) =>
            handleOpenSortDropdown(event, params.colDef.field)
          }
          aria-label={t('accessibility.table.sotingButton', {
            name: params.colDef.headerName,
          })}
          className="custom-icon-button"
          data-testid={`header-sorting-button-${params.colDef.headerName}`}
        >
          {sortIcon}
        </IconButton>
      );
    }
    return iconButton;
  };
  const handleOpenSortDropdown = (event: any, column: string) => {
    setAnchorEl(event.currentTarget);
    setActiveColumn(column);
  };

  // Custom header renderer for sorting
  const renderSortHeader = (params: any) => {
    return (
      <>
        <div
          className={
            params.colDef.field === 'select'
              ? 'aur-datagrid-checkbox-separator'
              : params.colDef.isBorderNotRequired
                ? ''
                : params.colDef.isSearchBoxRequired
                  ? 'sort-img-header-div aur-datagrid-search-box-column-separator'
                  : 'sort-img-header-div aur-datagrid-column-separator'
          }
        >
          <span
            className={`aur-datagrid-header-focusable ${
              params.colDef.isSearchBoxRequired
                ? 'searchbox-column-headername'
                : 'sort-column-headername'
            }`}
            tabIndex={defaultHeaderCellTabIndex ?? params.tabIndex}
          >
            {params.colDef.headerName ? (
              params.colDef.headerName
            ) : (
              <span className="sr-only">{t('common.select')}</span>
            )}
          </span>
          {params.colDef.isSearchBoxRequired && (
            <div className="sort-column-searchbox">
              <AurSearchBox
                onSearch={onSearch}
                value={searchValue ?? ''}
                searchLoading={searchLoading}
              />
            </div>
          )}
          {renderSortIcon(params)}
        </div>

        {activeColumn === params.colDef.field && (
          <AurCustomSort
            anchorEl={anchorEl}
            onClose={() => setAnchorEl(null)}
            onSort={(direction) => handleSort(params.colDef.field, direction)}
            sortModel={sortModel}
            activeColumn={activeColumn}
            ascFieldName={params.colDef.ascFieldName}
            descFieldName={params.colDef.descFieldName}
          />
        )}
      </>
    );
  };

  // Modify columns to add the custom sort dropdown and disable the sort button
  // const modifiedColumns: GridColDef[] = columns.map((column) => ({
  //   ...column,
  //   renderHeader: renderSortHeader,
  //   hideSortIcons: true,
  // }));

  // Modify other columns to apply rowspan logic
  const modifiedColumns: GridColDef[] = columns.map((column) => ({
    ...column,
    ...(column.field !== 'expand' &&
      isTreeView && {
        renderCell: (params) => {
          if (isTreeView) {
            const rowData = params.row as TreeViewRowData;
            const childrenCount = rowData.children
              ? rowData.children.length
              : 0;
            const shouldHaveRowSpan = rowData.isExpanded && childrenCount > 0;
            return (
              <div
                style={{
                  gridRowEnd: shouldHaveRowSpan
                    ? `span ${childrenCount + 1}`
                    : undefined,
                }}
              >
                {params.value}
              </div>
            );
          }
          return params.value;
        },
      }),
    renderHeader: column.renderHeader ?? renderSortHeader,
    hideSortIcons: true,
    cellClassName: (params) => {
      const existingClass =
        typeof column.cellClassName === 'function'
          ? column.cellClassName(params)
          : column.cellClassName || '';

      let classNames = '';
      if (existingClass && existingClass !== '') {
        classNames += `${existingClass} `;
      }

      if (isTreeView) {
        const rowData = params.row as TreeViewRowData;
        const isParent = !rowData.parentId;
        const isChild = rowData.parentId
          ? expandedRows.includes(rowData.parentId)
          : false;

        const hasData =
          params.value !== null &&
          params.value !== undefined &&
          params.value !== '';

        if (isParent) {
          classNames += 'parent-row-with-borders';
        }

        if (isChild && isTreeView) {
          classNames += 'expanded-cell';
          if (hasData) {
            classNames += ' expanded-cell-with-data';
          } else {
            classNames += ' empty-cell'; // Add empty-cell class for cells without data
          }
          if (
            params.field !== columns[0].field &&
            params.field !== columns[1].field
          ) {
            classNames += ' expanded-cell-except-first-second-column';
          }
          if (params.field === columns[5].field) {
            classNames += ' expanded-cell-last-column';
          }

          return classNames.trim();
        }
        if (!hasData) {
          classNames += ' empty-cell';
        } else {
          classNames += ' cell-with-data';
        }
        return classNames.trim();
      }
      return classNames.trim();
    },
  }));

  const [expandedRows, setExpandedRows] = useState<number[]>([]);

  const toggleRowExpansion = useCallback((id: number) => {
    setExpandedRows((prevExpanded) =>
      prevExpanded.includes(id)
        ? prevExpanded.filter((rowId) => rowId !== id)
        : [...prevExpanded, id]
    );
  }, []);

  const getProcessedData = (data: any[]): TreeViewRowData[] => {
    return data.reduce<TreeViewRowData[]>((acc, row) => {
      const isExpanded = expandedRows.includes(row.id);

      if (isExpanded && row.children && !row.originalData) {
        row.originalData = JSON.parse(JSON.stringify(row));
        row = moveParentDataToChildren([row], row.id)[0];
      }

      if (!isExpanded && row.originalData) {
        row = restoreOriginalData(row);
        row.children = row.children?.map((child: any) =>
          restoreOriginalData(child)
        );
      }
      acc.push({
        ...row,
        isExpanded,
        toggleExpansion: toggleRowExpansion,
      });

      if (isExpanded && row.children) {
        const childRows = row.children.map((child: any) => ({
          ...child,
          parentId: row.id,
          toggleExpansion: toggleRowExpansion,
        }));
        acc.push(...childRows);
      }

      return acc;
    }, []);
  };

  const renderCellWithTabIndex =
    (column: AurDataGridColDef) => (params: any) => {
      const {value} = params;
      const isVisibleTooltip = visibleToolTip && value; // Check if the tooltip should be visible

      const tabIndex = params.tabIndex < 0 ? 0 : params;

      const cellContent = (
        <span className="cell-truncate" tabIndex={tabIndex}>
          {value}
        </span>
      );
      return isVisibleTooltip ? (
        <LightTooltip
          title={column?.isDateColumn ? t('common.yearMonthDate') : value}
        >
          {cellContent}
        </LightTooltip>
      ) : (
        cellContent
      );
    };

  const withTabIndex = (
    renderCell: (params: GridRenderCellParams) => React.ReactNode
  ) => {
    return (params: GridRenderCellParams) => {
      const cellContent = renderCell(params); // Get the content of the cell
      let tabIndex = -1;

      if (
        isValidElement(cellContent) &&
        typeof cellContent.props?.tabIndex === 'number' &&
        cellContent.props.tabIndex > 0
      ) {
        tabIndex = cellContent.props.tabIndex;
      }
      const isDateField = (params.colDef as AurDataGridColDef).isDateColumn;
      const getTooltipText = (node: ReactNode): string => {
        if (!isValidElement(node)) return '';
        const children = React.Children.toArray(node.props.children);
        for (const child of children) {
          if (
            isValidElement(child) &&
            typeof child.props?.className === 'string' &&
            child.props.className.includes('typo-text')
          ) {
            return typeof child.props.children === 'string'
              ? child.props.children
              : '';
          } else if (typeof child === 'string') {
            if (isDateField && child === 'N/A') return '';
            return child;
          }
        }
        return '';
      };

      const tooltipText = getTooltipText(cellContent);

      const contentWrapper = (
        <span
          tabIndex={tabIndex < 0 && cellContent ? 0 : tabIndex}
          className="cell-truncate"
        >
          {cellContent}
        </span>
      );

      return visibleToolTip && tooltipText ? (
        <LightTooltip
          title={isDateField ? t('common.yearMonthDate') : tooltipText}
        >
          {contentWrapper}
        </LightTooltip>
      ) : (
        contentWrapper
      );
    };
  };

  const renderHeaderWithTabIndex = (renderHeader: any) => {
    return (params: any) => {
      return (
        <span tabIndex={headerSpanTabIndex ?? -1} style={{width: '100%'}}>
          {renderHeader(params)}
        </span>
      );
    };
  };

  const updatedColumns: any = modifiedColumns.map((col) => ({
    ...col,
    renderCell: col.renderCell
      ? withTabIndex(col.renderCell)
      : renderCellWithTabIndex(col),
    renderHeader: col.renderHeader
      ? renderHeaderWithTabIndex(col.renderHeader)
      : renderHeaderWithTabIndex,
  }));

  const idToRow = useMemo(
    () => Object.fromEntries(rows.map((row) => [row.id, row])),
    [rows]
  );

  const getRowHeight = useCallback(
    ({id, densityFactor}: GridRowHeightParams) => {
      const row = idToRow[id];
      return row?.type === INDEXING_SUB_INFO
        ? INDEXING_SUBINDEX_HEIGHT * densityFactor
        : null;
    },
    [idToRow]
  );

  const rowHeightCallback =
    entityType === INDEXING_HISTORY ? getRowHeight : undefined;

  return (
    <DataGrid
      data-testid="data-grid"
      className={`aur-datagrid ${className}`}
      rows={isTreeView ? getProcessedData([...rows]) : rows}
      getRowId={(row) => row[primaryKey]}
      columns={updatedColumns}
      checkboxSelection={checkBoxVisible}
      sortModel={sortModel}
      disableColumnMenu
      autoHeight
      hideFooter={shouldHidePagination || !showPagination}
      getRowHeight={rowHeightCallback}
      getRowClassName={(params) => {
        if (isTreeView) {
          const row = params.row as TreeViewRowData;
          const rowId = params.id as number;

          const isExpanded = expandedRows.includes(rowId);
          const isParentExpanded = row.parentId
            ? expandedRows.includes(row.parentId)
            : false;

          const parentRow = row.parentId
            ? rows.find((r) => r.id === row.parentId)
            : null;
          const isFirstChild = parentRow?.children?.[0]?.id === rowId;
          const isLastChild =
            parentRow?.children?.[parentRow.children.length - 1]?.id === rowId;
          let className =
            isExpanded || isParentExpanded ? 'expanded' : 'collapsed';
          if (isFirstChild) {
            className += ' expanded-first';
          }
          if (isLastChild) {
            className += ' expanded-last';
          }
          if (row.receivedDate === 'Totals') {
            className += ' total-row';
          }
          return className;
        } else if (indexTree) {
          let className;
          const type = params.row.type;
          if (type === 'child') {
            className = 'index-child-row';
            return className;
          }

          if (type === 'info') {
            className = 'sub-index-row';
            return className;
          }
        }
        return '';
      }}
      slots={{
        noRowsOverlay: noRowsOverlay,
        pagination: CustomPagination, // AurPagination component
        baseCheckbox: renderCheckbox,
      }}
      slotProps={{
        noRowsOverlay: {
          onRefresh,
          containerHeight,
        } as unknown as Record<string, unknown>,
      }}
      onRowSelectionModelChange={(newSelection) => {
        onSelectionChange?.(newSelection as number[]);
      }}
      keepNonExistentRowsSelected
      rowSelectionModel={selectedIds}
      initialState={{
        pagination: {
          paginationModel: {
            pageSize: isTreeView ? 100 : itemsPerPage,
          },
        },
        sorting: {
          sortModel: [{field: defaultSortColumnName, sort: defaultSortOrder}],
        },
      }}
      hideFooterSelectedRowCount={hideFooterSelectedRowCount}
      onRowClick={handleRowClick}
      sx={{cursor: pointer ? 'pointer' : 'default'}}
      onCellKeyDown={handleCellKeyDown}
      {...restProps}
    />
  );
};

export default memo(AurDataGrid);
